import type { To } from 'react-router';

import { Navigate } from 'react-router';

import { AdminPermissionsProvider, useAdminPermissionsQuery } from 'src/hooks/admin';

import { isServerError } from 'src/lib/errors';

import { LoadingScreen } from 'src/components/loading-screen';

import { View500, ErrorView } from 'src/sections/error';

import { useAuthContext } from '../hooks';

type PermissionKey =
  | 'canCreateUsers'
  | 'canRemoveUsers'
  | 'canDisableYearlyCreditRenewalForUser'
  | 'canAssignIndividualCe'
  | 'canSetSsoUserDetails'
  | 'canAccessUserManagement'
  | 'canAccessGroupManagement'
  | 'canManageAdminRoles';

export type PermissionBasedGuardProp = {
  requiredPermission: PermissionKey;
  children: React.ReactNode;
  redirectPath?: To;
};

export function PermissionBasedGuard({
  requiredPermission,
  children,
  redirectPath,
}: Readonly<PermissionBasedGuardProp>) {
  const resolvedRedirectPath: To = redirectPath ?? '/dashboard';

  const { isSwitchingRole } = useAuthContext();
  const { permissions, isLoading, error, refetch } = useAdminPermissionsQuery();

  if (isSwitchingRole || isLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    if (isServerError(error)) {
      return <View500 />;
    }
    return <ErrorView error={error} />;
  }

  if (!permissions) {
    return <Navigate to={resolvedRedirectPath} replace />;
  }

  if (!permissions[requiredPermission]) {
    return <Navigate to={resolvedRedirectPath} replace />;
  }

  return (
    <AdminPermissionsProvider permissions={permissions} refetch={refetch}>
      {children}
    </AdminPermissionsProvider>
  );
}
